/**
 * WarRoomPanel — War Room deliberation panel for the Analysis stage
 *
 * Phase 4.4 — redesign-p4-analatt-44b
 * Refactored from AttorneyWarRoomPage.tsx for panel embedding
 * Includes: consensus view, deliberation timeline, disputed issues list,
 * challenge-filing form, query interface
 * Wired to /deliberation/{report_id} (WAR_ROOM_USE_MCP is live in Railway)
 */

import React, { useEffect, useState } from 'react';
import { safeText } from '@/lib/safeText';
import {
  MessagesSquare,
  Gavel,
  ShieldCheck,
  Sparkles,
  Send,
  Loader2,
  X,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/Button';
import {
  getDeliberation,
  getIssues,
  submitHumanInput,
  CaseDeliberation,
  DisputedIssue,
  DeliberationEvent,
} from '@/services/deliberationApi';
import { api } from '@/utils/api';
import { BRAND_NAME } from '@/config/brand';

interface WarRoomPanelProps {
  reportId: string;
  showQuery?: boolean;
  showChallenges?: boolean;
}

interface ConsensusData {
  run_id: string;
  produced_at: string;
  settlement_range?: { low: number; high: number };
  recommended_value?: number;
  confidence?: number;
}

interface QueryAnswer {
  answer: string;
  referenced_events: string[];
  queries_remaining: number;
  question: string;
}

const ENGINE_LIST: { id: string; display: string }[] = [
  { id: 'merchant_reconciliation', display: 'P&L Reconciliation' },
  { id: 'merchant_revenue_augmentation', display: 'Revenue Augmentation' },
  { id: 'bottom_up', display: 'Bottom-Up Loss' },
  { id: 'enhanced_claim', display: 'Enhanced Claim' },
  { id: 'dual_method', display: 'Dual-Method Reconciler' },
  { id: 'mitigation', display: 'Mitigation Deductions' },
  { id: 'impact', display: 'Impact Analysis' },
  { id: 'epi', display: 'Extended Period of Indemnity' },
  { id: 'policy_deductions', display: 'Policy Deductions' },
  { id: 'pv', display: 'Present Value' },
  { id: 'fair_settlement', display: 'Fair Settlement' },
  { id: 'case_strength', display: 'Case Strength' },
];

function TimelineCard({ event, isLast }: { event: DeliberationEvent; isLast: boolean }) {
  const [expanded, setExpanded] = useState(false);

  const getAgentName = (name: string) => {
    const map: Record<string, string> = {
      'ai:forensic': 'Forensic Analyst',
      'ai:attorney': 'Attorney Advisor',
      'ai:adjuster': 'Adjuster Analyst',
      'human:accountant': 'Accountant',
      'human:attorney': 'Attorney',
    };
    return map[name] || name;
  };

  const getEventColor = (agentName: string) => {
    if (agentName.includes('forensic')) return 'bg-[var(--color-brand)]';
    if (agentName.includes('attorney')) return 'bg-[var(--color-verified)]';
    if (agentName.includes('adjuster')) return 'bg-[var(--color-text-muted)]';
    return 'bg-[var(--color-text-dim)]';
  };

  return (
    <div className="relative flex gap-4">
      {!isLast && (
        <div className="absolute left-[11px] top-6 h-full w-0.5 bg-[var(--color-border-sub)]" />
      )}
      <div className={`h-6 w-6 flex-shrink-0 rounded-full ${getEventColor(event.agent_name)}`} />
      <div className="flex-1 pb-4">
        <div className="rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-bg-elevated)] p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[var(--color-text-primary)]">
                {getAgentName(event.agent_name)}
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">
                {new Date(event.timestamp).toLocaleTimeString()}
              </span>
            </div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-sm text-[var(--color-text-secondary)]">{event.reasoning.slice(0, 150)}…</p>
          {expanded && (
            <div className="mt-3 space-y-2 border-t border-[var(--color-border-faint)] pt-3 text-xs">
              <p className="text-[var(--color-text-muted)]">
                <span className="font-medium">Event Type:</span> {event.event_type}
              </p>
              {event.tool_name && (
                <p className="text-[var(--color-text-muted)]">
                  <span className="font-medium">Tool:</span> {event.tool_name}
                </p>
              )}
              {safeText(event.tool_result_summary) && (
                <p className="text-[var(--color-text-muted)]">
                  <span className="font-medium">Result:</span> {safeText(event.tool_result_summary)}
                </p>
              )}
              {event.issue_tag && (
                <p className="text-[var(--color-text-muted)]">
                  <span className="font-medium">Issue:</span> {event.issue_tag}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IssueCard({ issue, reportId, onUpdate }: { issue: DisputedIssue; reportId: string; onUpdate: () => void }) {
  const [showInput, setShowInput] = useState(false);
  const [inputText, setInputText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!inputText.trim()) return;
    setSubmitting(true);
    try {
      await submitHumanInput(reportId, {
        issue_tag: issue.tag,
        content: inputText,
        author_role: 'attorney',
        author_name: 'Attorney',
      });
      setShowInput(false);
      setInputText('');
      onUpdate();
    } catch (err) {
      console.error('Failed to submit input:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusVariant = (status: string): Parameters<typeof StatusBadge>[0]['variant'] => {
    switch (status) {
      case 'resolved':
        return 'done';
      case 'human_override':
        return 'active';
      default:
        return 'attention';
    }
  };

  return (
    <div className="rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-bg-elevated)] p-4">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <h4 className="font-medium text-[var(--color-text-primary)]">{issue.title}</h4>
          <p className="text-xs text-[var(--color-text-muted)]">{issue.tag}</p>
        </div>
        <StatusBadge text={issue.status.replace('_', ' ')} variant={getStatusVariant(issue.status)} size="sm" />
      </div>

      <p className="mb-3 text-sm text-[var(--color-text-secondary)]">{issue.description}</p>

      {/* Agent Positions */}
      {Object.entries(issue.agent_positions).length > 0 && (
        <div className="mb-3 space-y-2">
          <p className="text-xs font-medium text-[var(--color-text-muted)]">Agent Positions:</p>
          {Object.entries(issue.agent_positions).map(([agent, position], idx) => (
            <div key={idx} className="flex items-center justify-between rounded bg-[var(--color-bg-base)] px-2 py-1 text-xs">
              <span className="text-[var(--color-text-secondary)]">{agent}</span>
              <div className="flex items-center gap-2">
                {position.value && <FinancialValue value={parseFloat(position.value)} />}
                <span className="text-[var(--color-text-muted)]">{(position.confidence || 0) * 100}% conf</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Resolution */}
      {issue.resolution && (
        <div className="mb-3 rounded-md bg-transparent p-2">
          <p className="text-xs font-medium text-[var(--color-verified)]">Resolution: {issue.resolution.method}</p>
          <p className="text-xs text-[var(--color-text-secondary)]">{issue.resolution.explanation}</p>
        </div>
      )}

      {/* Human Input Form */}
      {showInput ? (
        <div className="space-y-2">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Add your input on this issue..."
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
            rows={2}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowInput(false)}
              className="rounded-md px-3 py-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !inputText.trim()}
              className="rounded-md bg-[var(--color-brand)] px-3 py-1 text-xs font-medium text-[var(--color-on-brand)] disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Input'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowInput(true)}
          className="text-xs text-[var(--color-brand)] hover:underline"
        >
          + Add input
        </button>
      )}
    </div>
  );
}

function ChallengeModal({
  reportId,
  engine,
  onClose,
  onSuccess,
}: {
  reportId: string;
  engine: { id: string; display: string };
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [proposedChange, setProposedChange] = useState('');
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = proposedChange.length >= 20 && rationale.length >= 50 && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/api/v1/attorney/challenge/${reportId}`, {
        target_engine_id: engine.id,
        proposed_change: proposedChange,
        rationale,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to submit challenge');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gavel className="h-5 w-5 text-[var(--color-brand)]" />
            <h3 className="font-medium text-[var(--color-text-primary)]">Challenge: {engine.display}</h3>
          </div>
          <button onClick={onClose} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-text-muted)]">
              Proposed Change ({proposedChange.length}/20 min)
            </label>
            <textarea
              value={proposedChange}
              onChange={(e) => setProposedChange(e.target.value)}
              placeholder="Describe what should change..."
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
              rows={3}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs text-[var(--color-text-muted)]">
              Rationale ({rationale.length}/50 min)
            </label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Explain why this engine output is incorrect..."
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
              rows={4}
            />
          </div>

          {error && (
            <div className="rounded-md bg-[rgba(255,102,0,0.08)] p-3 text-sm text-[var(--color-brand)]">{error}</div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-[var(--color-on-brand)] disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'File Challenge'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function WarRoomPanel({ reportId, showQuery = true, showChallenges = true }: WarRoomPanelProps) {
  const [deliberation, setDeliberation] = useState<CaseDeliberation | null>(null);
  const [consensus, setConsensus] = useState<ConsensusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEngine, setSelectedEngine] = useState<{ id: string; display: string } | null>(null);
  const [question, setQuestion] = useState('');
  const [queryHistory, setQueryHistory] = useState<QueryAnswer[]>([]);
  const [submittingQuery, setSubmittingQuery] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [delibData, consensusData] = await Promise.all([
        getDeliberation(reportId).catch(() => null),
        api.get<ConsensusData>(`/api/v1/attorney/war-room/${reportId}/consensus`).catch(() => null),
      ]);
      setDeliberation(delibData);
      setConsensus(consensusData);
    } catch (err: any) {
      setError(err?.message || 'Failed to load War Room data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [reportId]);

  const handleQuery = async () => {
    if (!question.trim() || question.length < 10) return;
    setSubmittingQuery(true);
    try {
      const response = await api.post<Omit<QueryAnswer, 'question'>>(`/api/v1/attorney/war-room/${reportId}/query`, {
        question: question.trim(),
      });
      setQueryHistory((prev) => [...prev, { ...response, question: question.trim() }]);
      setQuestion('');
    } catch (err: any) {
      console.error('Query failed:', err);
    } finally {
      setSubmittingQuery(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-[var(--color-brand)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-[rgba(255,102,0,0.08)] p-4">
        <p className="text-sm text-[var(--color-brand)]">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Consensus Card */}
      {consensus && (
        <div className="rounded-xl border border-[var(--color-needs-human-tint-bd)] bg-[var(--color-needs-human-tint)] p-5">
          <div className="mb-4 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-[var(--color-brand)]" />
            <h3
              className="font-semibold text-[var(--color-brand)]"
              style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
            >
              War Room Consensus
            </h3>
          </div>

          {consensus.settlement_range && (
            <div className="mb-4 grid grid-cols-2 gap-4">
              <MetricCard
                label="Settlement Range (Low)"
                value={<FinancialValue value={consensus.settlement_range.low} />}
              />
              <MetricCard
                label="Settlement Range (High)"
                value={<FinancialValue value={consensus.settlement_range.high} />}
                accent
              />
            </div>
          )}

          {consensus.recommended_value && (
            <div className="flex items-center justify-between rounded-lg bg-[var(--color-bg-surface)] p-3">
              <span className="text-sm text-[var(--color-text-secondary)]">Recommended Value</span>
              <FinancialValue value={consensus.recommended_value} size="lg" />
            </div>
          )}

          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            Run: {consensus.run_id} • Produced: {new Date(consensus.produced_at).toLocaleString()}
          </p>
        </div>
      )}

      {/* Query Interface */}
      {showQuery && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
          <div className="mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[var(--color-brand)]" />
            <h3 className="font-medium text-[var(--color-text-primary)]">Ask a Question</h3>
          </div>

          {queryHistory.length > 0 && (
            <div className="mb-4 max-h-48 space-y-3 overflow-y-auto">
              {queryHistory.map((item, idx) => (
                <div key={idx} className="rounded-lg bg-[var(--color-bg-elevated)] p-3">
                  <p className="mb-1 text-xs text-[var(--color-brand)]">Q: {item.question}</p>
                  <p className="text-sm text-[var(--color-text-secondary)]">{item.answer}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g., How was the revenue gap resolved?"
              className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-secondary)]"
              onKeyDown={(e) => e.key === 'Enter' && handleQuery()}
            />
            <button
              onClick={handleQuery}
              disabled={submittingQuery || question.length < 10}
              className="flex items-center gap-2 rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-[var(--color-on-brand)] disabled:opacity-50"
            >
              {submittingQuery ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Ask
            </button>
          </div>
        </div>
      )}

      {/* Challenge Section */}
      {showChallenges && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
          <div className="mb-3 flex items-center gap-2">
            <Gavel className="h-4 w-4 text-[var(--color-brand)]" />
            <h3 className="font-medium text-[var(--color-text-primary)]">File a Challenge</h3>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {ENGINE_LIST.map((engine) => (
              <button
                key={engine.id}
                onClick={() => setSelectedEngine(engine)}
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-left text-xs text-[var(--color-text-secondary)] hover:border-[var(--color-brand)] hover:text-[var(--color-brand)]"
              >
                {engine.display}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Disputed Issues */}
      {deliberation?.issues && deliberation.issues.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
          <div className="mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-[var(--color-brand)]" />
            <h3
              className="font-semibold text-[var(--color-text-primary)]"
              style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
            >
              Disputed Issues ({deliberation.issues.length})
            </h3>
          </div>

          <div className="space-y-3">
            {deliberation.issues.map((issue, idx) => (
              <IssueCard key={issue.tag || idx} issue={issue} reportId={reportId} onUpdate={fetchData} />
            ))}
          </div>
        </div>
      )}

      {/* Deliberation Timeline */}
      {deliberation?.events && deliberation.events.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
          <div className="mb-4 flex items-center gap-2">
            <MessagesSquare className="h-5 w-5 text-[var(--color-brand)]" />
            <h3
              className="font-semibold text-[var(--color-text-primary)]"
              style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
            >
              Deliberation Timeline ({deliberation.events.length} events)
            </h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {deliberation.events.map((event, idx) => (
              <TimelineCard
                key={idx}
                event={event}
                isLast={idx === deliberation.events.length - 1}
              />
            ))}
          </div>
        </div>
      )}

      {/* Challenge Modal */}
      {selectedEngine && (
        <ChallengeModal
          reportId={reportId}
          engine={selectedEngine}
          onClose={() => setSelectedEngine(null)}
          onSuccess={fetchData}
        />
      )}
    </div>
  );
}

export default WarRoomPanel;
